const SLOT_MINUTES = 30;
const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;
/** What the upgrader enforces when ``/status`` predates ``schedule_window``. */
export const DEFAULT_WINDOW = { min_lead_minutes: 10, max_days: 7 };

function parts(date, options) {
    return Object.fromEntries(
        new Intl.DateTimeFormat('en-US', options).formatToParts(date).map(({ type, value }) => [type, value])
    );
}

/** ``"2:00am"`` */
function clock(date) {
    const { hour, minute, dayPeriod } = parts(date, { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${hour}:${minute}${dayPeriod.toLowerCase()}`;
}

/** ``"2:00 AM"``, as Figma writes times in the schedule modal and the bell. */
function clockSpaced(date) {
    const { hour, minute, dayPeriod } = parts(date, { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${hour}:${minute} ${dayPeriod}`;
}

/** ``"3 Nov 2026 at 2:00am"`` in the browser's time zone. */
export function formatScheduledAt(iso) {
    const at = new Date(iso);
    const { day, month, year } = parts(at, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${day} ${month} ${year} at ${clock(at)}`;
}

/** ``"Mon, 3 Nov at 2:00 AM"`` (Figma 91:16636). */
export function formatShortScheduledAt(iso) {
    const at = new Date(iso);
    const { weekday, day, month } = parts(at, { weekday: 'short', day: 'numeric', month: 'short' });
    return `${weekday}, ${day} ${month} at ${clockSpaced(at)}`;
}

/** ``"2 hours ago"``; ``"Just now"`` under a minute (Figma 91:13919). */
export function formatAgo(iso, now = new Date()) {
    const seconds = Math.round((now - new Date(iso)) / 1000);
    if (seconds < 60) return 'Just now';
    const format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    for (const [unit, size] of [
        ['day', 86_400],
        ['hour', 3_600],
        ['minute', 60],
    ]) {
        if (seconds >= size) return format.format(-Math.floor(seconds / size), unit);
    }
    return 'Just now';
}

function startOfDay(date) {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
}

/** The local ``Date`` for a calendar day plus a ``'HH:mm'`` slot. */
export function combine(day, slot) {
    const [hours, minutes] = slot.split(':').map(Number);
    const at = startOfDay(day);
    at.setHours(hours, minutes);
    return at;
}

export function tomorrow(now = new Date()) {
    const day = startOfDay(now);
    day.setDate(day.getDate() + 1);
    return day;
}

/** Earliest and latest times the upgrader accepts, from ``/status``'s ``schedule_window``. */
export function scheduleBounds(window = DEFAULT_WINDOW, now = new Date()) {
    return {
        earliest: new Date(now.getTime() + window.min_lead_minutes * MINUTE_MS),
        latest: new Date(now.getTime() + window.max_days * DAY_MS),
    };
}

/** The half-hour slots of ``day`` inside ``bounds``, as ``{value: 'HH:mm', label: 'h:mm AM'}``. */
export function slotsFor(day, { earliest, latest }) {
    return Array.from({ length: (24 * 60) / SLOT_MINUTES }, (_, index) => {
        const value = `${String(Math.floor((index * SLOT_MINUTES) / 60)).padStart(2, '0')}:${String(
            (index * SLOT_MINUTES) % 60
        ).padStart(2, '0')}`;
        return { value, at: combine(day, value) };
    })
        .filter(({ at }) => at >= earliest && at <= latest)
        .map(({ value, at }) => ({ value, label: clockSpaced(at) }));
}
